export const M10_MODULE_REVIEW = {
  id: "m10-review-general",
  title: "Módulo 10 — Review General",
  topic: "Reported Speech · Reported Questions · If/Whether · Indirect Questions · Wishes & Regrets",
  iconType: "star",

  sets: [

    // ===================================================================
    // REVIEW SET 1 — CORE STRUCTURES (recognition & identification)
    // ===================================================================
    {
      id: "m10-rev-set1",
      title: "Review Set 1: Core Structures",
      context: `*All five units — identifying and recognising the key grammar structures of Module 10.*`,

      questions: [

        // --- UNIT 11 ---
        {
          type: "mc",
          q: "Unit 11 — Which sentence is correctly reported?",
          options: [
            "She said that she was tired.",
            "She said that she is tired.",
            "She said that she were tired."
          ],
          a: "She said that she was tired.",
          tip: "Direct: 'I am tired.' → Reported: she said that she WAS tired. Present simple (am) → past simple (was). Backshift rule."
        },
        {
          type: "mc",
          q: "Unit 11 — What is the difference between SAY and TELL in reported speech?",
          options: [
            "TELL is followed by an object (told her that). SAY is not (said that).",
            "SAY is followed by an object. TELL is not.",
            "There is no difference — both can be used identically."
          ],
          a: "TELL is followed by an object (told her that). SAY is not (said that).",
          tip: "'She told HER that...' ✅ 'She said HER that...' ❌ — TOLD always needs an object. SAY does not."
        },

        // --- UNIT 12 ---
        {
          type: "mc",
          q: "Unit 12 — Which is the correct reported question?",
          options: [
            "He asked where she lived.",
            "He asked where did she live.",
            "He asked where does she live."
          ],
          a: "He asked where she lived.",
          tip: "No inversion in reported questions! 'Where does she live?' → 'asked where she lived.' The auxiliary DO disappears. Subject + verb order."
        },
        {
          type: "mc",
          q: "Unit 12 — How do you report a yes/no question?",
          options: [
            "Use ASK + IF or WHETHER + subject + verb (no inversion).",
            "Use ASK + THAT + subject + verb.",
            "Use SAY + IF + subject + verb."
          ],
          a: "Use ASK + IF or WHETHER + subject + verb (no inversion).",
          tip: "'Are you ready?' → 'He asked if/whether she was ready.' IF or WHETHER — both correct. No DO, no inversion."
        },

        // --- UNIT 13 ---
        {
          type: "mc",
          q: "Unit 13 — What does IF/WHETHER introduce in 'She didn't know whether to go'?",
          options: [
            "An embedded clause expressing uncertainty (object of KNOW).",
            "A conditional sentence (if = in the case that).",
            "A reported question."
          ],
          a: "An embedded clause expressing uncertainty (object of KNOW).",
          tip: "KNOW WHETHER / WONDER IF — these introduce embedded clauses of uncertainty. Not a conditional, not a reported question. 'Whether to go' = what to do."
        },
        {
          type: "mc",
          q: "Unit 13 — Which sentence uses a verb + preposition collocation correctly?",
          options: [
            "She has always believed in following her instincts.",
            "She has always believed on following her instincts.",
            "She has always believed to follow her instincts."
          ],
          a: "She has always believed in following her instincts.",
          tip: "BELIEVE IN + gerund ✅ — BELIEVE ON ❌ — BELIEVE + infinitive ❌. Verb + preposition collocations must be learned as fixed pairs."
        },

        // --- UNIT 14 ---
        {
          type: "mc",
          q: "Unit 14 — Which is a correct indirect question?",
          options: [
            "Could you tell me where the station is?",
            "Could you tell me where is the station?",
            "Could you tell me where the station?"
          ],
          a: "Could you tell me where the station is?",
          tip: "Indirect questions use SUBJECT + VERB order (no inversion): 'where the station IS' ✅ — NOT 'where IS the station' ❌"
        },
        {
          type: "mc",
          q: "Unit 14 — Why do we use indirect questions?",
          options: [
            "To sound more polite, especially in formal or public contexts.",
            "Because they are grammatically simpler than direct questions.",
            "Because direct questions are considered incorrect in English."
          ],
          a: "To sound more polite, especially in formal or public contexts.",
          tip: "'Where is the station?' = direct (neutral). 'Could you tell me where the station is?' = indirect (polite). Same information, different register."
        },

        // --- UNIT 15 ---
        {
          type: "mc",
          q: "Unit 15 — What is the difference between WISH + past simple and WISH + past perfect?",
          options: [
            "Past simple = present unreal wish. Past perfect = past regret.",
            "Past simple = past regret. Past perfect = present wish.",
            "Both express the same meaning — only the tense changes."
          ],
          a: "Past simple = present unreal wish. Past perfect = past regret.",
          tip: "'I wish I worked in art' = I don't work there NOW (present unreal). 'I wish I had studied art' = I didn't study it IN THE PAST (past regret)."
        },
        {
          type: "mc",
          q: "Unit 15 — What is the key difference between WISH and HOPE?",
          options: [
            "WISH = unreal or impossible. HOPE = still genuinely possible.",
            "HOPE = unreal or impossible. WISH = still genuinely possible.",
            "WISH and HOPE mean the same — just different formality levels."
          ],
          a: "WISH = unreal or impossible. HOPE = still genuinely possible.",
          tip: "'I wish I could fly' = impossible. 'I hope I can do it' = still possible. Grammar reveals your belief: wish = no, hope = maybe yes."
        },

        // --- CROSS-UNIT ---
        {
          type: "mc",
          q: "Module 10 — Which sentence combines reported speech (Unit 11) and a wish (Unit 15)?",
          options: [
            "She said that she wished she had stayed.",
            "She asked whether she should stay.",
            "She wondered if she would stay."
          ],
          a: "She said that she wished she had stayed.",
          tip: "SAID THAT (Unit 11 reported speech) + WISHED SHE HAD STAYED (Unit 15 past regret). Two units in one sentence — the most natural kind of real English."
        },
        {
          type: "mc",
          q: "Module 10 — Which sentence combines an indirect question (Unit 14) and IF/WHETHER (Unit 13)?",
          options: [
            "Do you know whether she has decided yet?",
            "She asked if she had decided yet.",
            "I wish I knew whether she had decided."
          ],
          a: "Do you know whether she has decided yet?",
          tip: "DO YOU KNOW (indirect question, Unit 14) + WHETHER she has decided (Unit 13 embedded clause). 'I wish I knew' = Unit 15 + 13, also valid but not the target combination here."
        }
      ]
    },

    // ===================================================================
    // REVIEW SET 2 — TENSE BACKSHIFT & TRANSFORMATIONS
    // ===================================================================
    {
      id: "m10-rev-set2",
      title: "Review Set 2: Tense Backshift & Transformations",
      context: `*Units 11 & 12 — Applying tense backshift rules and transforming direct → reported speech and questions.*`,

      questions: [

        {
          type: "mc",
          q: "Direct: 'I am working late tonight.' → Reported:",
          options: [
            "She said that she was working late that night.",
            "She said that she is working late tonight.",
            "She said that she worked late tonight."
          ],
          a: "She said that she was working late that night.",
          tip: "Present continuous (am working) → past continuous (was working). Tonight → that night. Two backshifts: tense + time expression."
        },
        {
          type: "mc",
          q: "Direct: 'I have finished the report.' → Reported:",
          options: [
            "He said that he had finished the report.",
            "He said that he has finished the report.",
            "He said that he finished the report."
          ],
          a: "He said that he had finished the report.",
          tip: "Present perfect (have finished) → past perfect (had finished). This is the most common backshift in professional and academic English."
        },
        {
          type: "mc",
          q: "Direct: 'We will call you tomorrow.' → Reported:",
          options: [
            "They said that they would call me the following day.",
            "They said that they will call me tomorrow.",
            "They said that they called me the following day."
          ],
          a: "They said that they would call me the following day.",
          tip: "Will → would (modal backshift). Tomorrow → the following day (time expression backshift). Both must change."
        },
        {
          type: "mc",
          q: "Direct: 'Did you understand the lesson?' → Reported:",
          options: [
            "She asked me if I had understood the lesson.",
            "She asked me did I understand the lesson.",
            "She asked me if I understood the lesson."
          ],
          a: "She asked me if I had understood the lesson.",
          tip: "Past simple question (did understand) → past perfect in reported (had understood). No inversion, no DO. IF introduces the embedded yes/no question."
        },
        {
          type: "mc",
          q: "Direct: 'Where are you going?' → Reported:",
          options: [
            "He asked where I was going.",
            "He asked where was I going.",
            "He asked where I am going."
          ],
          a: "He asked where I was going.",
          tip: "Present continuous (are going) → past continuous (was going). No inversion: WHERE + subject (I) + verb (was going). No auxiliary."
        },
        {
          type: "mc",
          q: "Direct: 'I don't know the answer.' → Reported:",
          options: [
            "She said that she didn't know the answer.",
            "She said that she doesn't know the answer.",
            "She said that she hadn't known the answer."
          ],
          a: "She said that she didn't know the answer.",
          tip: "Present simple negative (don't know) → past simple negative (didn't know). Straightforward backshift — no change needed to past perfect here."
        },
        {
          type: "mc",
          q: "Direct: 'I was here last year.' → Reported (backshift needed?)",
          options: [
            "She said she had been there the previous year. (Past simple → past perfect — full backshift).",
            "She said she was there last year. (No backshift — past simple stays).",
            "Both are grammatically possible depending on context."
          ],
          a: "Both are grammatically possible depending on context.",
          tip: "Past simple CAN stay as past simple in reported speech — backshift to past perfect is optional (not obligatory) for past simple. Both forms are correct."
        },
        {
          type: "mc",
          q: "Which time expression backshift is CORRECT?",
          options: [
            "Yesterday → the day before / the previous day.",
            "Yesterday → the next day.",
            "Yesterday → the following day."
          ],
          a: "Yesterday → the day before / the previous day.",
          tip: "Time expressions in reported speech: yesterday → the day before / the previous day. Tomorrow → the next day / the following day. Today → that day."
        },
        {
          type: "mc",
          q: "Direct: 'Can you help me?' → Reported:",
          options: [
            "She asked if I could help her.",
            "She asked if I can help her.",
            "She asked could I help her."
          ],
          a: "She asked if I could help her.",
          tip: "Can → could (modal backshift). Yes/no question → IF + subject + verb (no inversion, no CAN at the start). 'Could I help her' ❌ — no inversion."
        },
        {
          type: "mc",
          q: "When is backshift NOT required in reported speech?",
          options: [
            "When the reported statement describes a fact that is still true at the time of reporting.",
            "When the reporting verb is in the present tense (e.g. says, tells).",
            "Both A and B are correct reasons to omit backshift."
          ],
          a: "Both A and B are correct reasons to omit backshift.",
          tip: "No backshift needed when: (1) the fact is still true now ('She said the Earth is round' ✅), or (2) the reporting verb is present tense ('She says she is tired' ✅)."
        },
        {
          type: "mc",
          q: "Direct: 'I've never been to Japan.' → Reported correctly:",
          options: [
            "He said he had never been to Japan.",
            "He said he never went to Japan.",
            "He said he has never been to Japan."
          ],
          a: "He said he had never been to Japan.",
          tip: "Present perfect (have never been) → past perfect (had never been). 'Never went' = past simple, possible but changes the nuance. 'Has never been' = no backshift ❌"
        },
        {
          type: "mc",
          q: "Direct: 'We bought this house two years ago.' → Reported:",
          options: [
            "They said they had bought the house two years before / previously.",
            "They said they bought the house two years ago.",
            "Both are correct — ago and two years before are both acceptable."
          ],
          a: "Both are correct — ago and two years before are both acceptable.",
          tip: "AGO → two years before / previously (strict backshift). But AGO can also remain unchanged in informal speech. Both are widely accepted."
        }
      ]
    },

    // ===================================================================
    // REVIEW SET 3 — EMBEDDED CLAUSES & COLLOCATIONS
    // ===================================================================
    {
      id: "m10-rev-set3",
      title: "Review Set 3: Embedded Clauses & Collocations",
      context: `*Units 13 & 14 — If/whether clauses, verb + preposition collocations, and indirect question structures.*`,

      questions: [

        {
          type: "mc",
          q: "Unit 13 — Which sentence uses WONDER IF correctly?",
          options: [
            "I often wonder if I made the right decision.",
            "I often wonder if did I make the right decision.",
            "I often wonder if I will make the right decision yesterday."
          ],
          a: "I often wonder if I made the right decision.",
          tip: "WONDER IF + subject + verb (no inversion, no auxiliary). 'Wonder if I made' ✅ — 'wonder if did I make' ❌ (inversion error)."
        },
        {
          type: "mc",
          q: "Unit 13 — 'She isn't sure ___ to accept the offer.'",
          options: [
            "whether",
            "if",
            "Both whether and if are correct here."
          ],
          a: "whether",
          tip: "SURE WHETHER TO + infinitive ✅ — 'sure if to' ❌. When followed by a TO-infinitive (not a full clause), only WHETHER works."
        },
        {
          type: "mc",
          q: "Unit 13 — Which verb + preposition collocation is WRONG?",
          options: [
            "She insisted in leaving early.",
            "She insisted on leaving early.",
            "He dreamed of becoming an artist."
          ],
          a: "She insisted in leaving early.",
          tip: "INSIST ON (not in) + gerund ✅. DREAM OF + gerund ✅. INSIST IN ❌ — common error. The preposition must be memorised as part of the collocation."
        },
        {
          type: "mc",
          q: "Unit 13 — Complete: 'He ___ about quitting his job for months.'",
          options: [
            "had been thinking",
            "had been thinking about",
            "was thinking"
          ],
          a: "had been thinking",
          tip: "THINK ABOUT — the preposition ABOUT is already in the collocation. 'He had been thinking about quitting' ✅ — 'thinking about about quitting' ❌ — don't double the preposition."
        },
        {
          type: "mc",
          q: "Unit 14 — Transform: 'What time does the museum open?' → Indirect:",
          options: [
            "Do you know what time the museum opens?",
            "Do you know what time does the museum open?",
            "Could you tell me what time opens the museum?"
          ],
          a: "Do you know what time the museum opens?",
          tip: "Indirect WH-question: DO YOU KNOW + what time + subject (museum) + verb (opens). No inversion, no DO/DOES. Present simple in indirect form."
        },
        {
          type: "mc",
          q: "Unit 14 — Which indirect question opener is MOST formal?",
          options: [
            "Would you mind telling me where the exit is?",
            "Do you know where the exit is?",
            "Could you tell me where the exit is?"
          ],
          a: "Would you mind telling me where the exit is?",
          tip: "WOULD YOU MIND TELLING ME = most formal/polite. COULD YOU TELL ME = formal. DO YOU KNOW = neutral-polite. All are indirect questions, different levels of formality."
        },
        {
          type: "mc",
          q: "Unit 14 — 'I was wondering ___ you could help me.'",
          options: [
            "if",
            "that",
            "what"
          ],
          a: "if",
          tip: "I WAS WONDERING IF — the softening indirect structure. 'Was wondering if' introduces a yes/no embedded question — the most polite way to make a request."
        },
        {
          type: "mc",
          q: "Unit 13+14 — What do IF/WHETHER in Unit 13 and indirect questions in Unit 14 have in common?",
          options: [
            "Both use subject + verb order (no inversion) after if/whether/wh-word.",
            "Both always require backshift of the verb tense.",
            "Both can only be used in written, formal English."
          ],
          a: "Both use subject + verb order (no inversion) after if/whether/wh-word.",
          tip: "The core rule linking Units 13 and 14: EMBEDDED CLAUSES always use subject + verb order. No inversion, no auxiliary DO/DOES. 'If she was ready' ✅ — 'if was she ready' ❌"
        },
        {
          type: "mc",
          q: "Unit 13 — 'It depends ___ the weather.' Complete with the correct preposition.",
          options: [
            "on",
            "of",
            "about"
          ],
          a: "on",
          tip: "DEPEND ON ✅ — 'depend of' ❌ — 'depend about' ❌. A fixed collocation: depend ON. Also: 'It depends on whether we go or not' — DEPEND ON + WHETHER."
        },
        {
          type: "mc",
          q: "Unit 14 — Which sentence is INCORRECT?",
          options: [
            "I'd like to know does he work here.",
            "I'd like to know if he works here.",
            "I'd like to know whether he works here."
          ],
          a: "I'd like to know does he work here.",
          tip: "I'D LIKE TO KNOW + if/whether + subject + verb ✅ — NOT 'does he work' (inversion ❌). The auxiliary DOES disappears in embedded clauses."
        },
        {
          type: "mc",
          q: "Unit 13 — 'She ___ on becoming a doctor from a very young age.' Which verb completes this?",
          options: [
            "had set her heart",
            "had dreamed",
            "had insisted"
          ],
          a: "had set her heart",
          tip: "SET ONE'S HEART ON + gerund — a fixed collocation meaning 'to be determined to achieve something.' DREAM OF is also correct but requires 'of': 'dreamed of becoming.'"
        },
        {
          type: "mc",
          q: "Units 13+14 combined — Which sentence is correct?",
          options: [
            "Could you tell me whether the office is open today?",
            "Could you tell me whether is the office open today?",
            "Could you tell me if the office open today?"
          ],
          a: "Could you tell me whether the office is open today?",
          tip: "COULD YOU TELL ME (Unit 14 indirect) + WHETHER + subject + verb (Unit 13 embedded clause). 'Whether IS the office' = inversion ❌. 'If the office open' = missing verb form ❌."
        }
      ]
    },

    // ===================================================================
    // REVIEW SET 4 — WISHES, REGRETS & HOPE
    // ===================================================================
    {
      id: "m10-rev-set4",
      title: "Review Set 4: Wishes, Regrets & Hope",
      context: `*Unit 15 — Full review of wish + past simple / past perfect / could · if only · wish vs hope.*`,

      questions: [

        {
          type: "mc",
          q: "Situation: He lives in a small flat and wants a bigger one. Which wish is correct?",
          options: [
            "He wishes he lived in a bigger flat.",
            "He wishes he had lived in a bigger flat.",
            "He hopes he lived in a bigger flat."
          ],
          a: "He wishes he lived in a bigger flat.",
          tip: "CURRENT situation → WISH + past simple. 'He lives in a small flat' → 'he wishes he LIVED in a bigger one.' Past simple = present unreal wish."
        },
        {
          type: "mc",
          q: "Situation: She didn't study for the exam and failed. Which sentence expresses her regret?",
          options: [
            "She wishes she had studied for the exam.",
            "She wishes she studied for the exam.",
            "She wishes she could study for the exam."
          ],
          a: "She wishes she had studied for the exam.",
          tip: "PAST event she regrets → WISH + past perfect. 'She didn't study' → 'she wishes she HAD studied.' Past perfect = past regret."
        },
        {
          type: "mc",
          q: "Situation: He can't drive and really wants to. Which wish is correct?",
          options: [
            "He wishes he could drive.",
            "He wishes he drove.",
            "He hopes he could drive."
          ],
          a: "He wishes he could drive.",
          tip: "IMPOSSIBLE or UNAVAILABLE ability → WISH + COULD. 'He can't drive' → 'he wishes he COULD drive.' WISH + COULD = currently impossible ability."
        },
        {
          type: "mc",
          q: "Which sentence uses IF ONLY correctly?",
          options: [
            "If only I had listened to her advice!",
            "If only I listened to her advice!",
            "If only I will listen to her advice!"
          ],
          a: "If only I had listened to her advice!",
          tip: "IF ONLY + past perfect = emphatic PAST regret. 'If only I HAD listened' = I didn't listen (reality is the opposite). WILL after if only ❌."
        },
        {
          type: "mc",
          q: "What is the REALITY behind 'I wish I didn't give up so easily'?",
          options: [
            "I DO give up easily — it's my current habit.",
            "I GAVE UP once in the past — I regret it.",
            "I WILL give up — I'm predicting my own failure."
          ],
          a: "I DO give up easily — it's my current habit.",
          tip: "WISH + DIDN'T (past simple negative) = present unreal. Reality = the opposite: I DO give up easily. It's a current habit wish."
        },
        {
          type: "mc",
          q: "Choose the correct sentence for someone who is STILL able to change their situation:",
          options: [
            "I hope I can still make it work.",
            "I wish I could still make it work.",
            "If only I could still make it work."
          ],
          a: "I hope I can still make it work.",
          tip: "HOPE + CAN = real possibility (it's still achievable). WISH + COULD = currently impossible. IF ONLY + COULD = emphatic impossibility. The situation IS still possible, so HOPE is right."
        },
        {
          type: "mc",
          q: "Which sentence expresses the MOST emphatic regret?",
          options: [
            "If only I hadn't said that!",
            "I wish I hadn't said that.",
            "I regret saying that."
          ],
          a: "If only I hadn't said that!",
          tip: "IF ONLY = more emphatic than WISH. Both express past regret (hadn't said = past perfect), but IF ONLY carries stronger emotion. Often used with an exclamation mark."
        },
        {
          type: "mc",
          q: "'She wishes she ___ married so young.' Which tense is needed?",
          options: [
            "hadn't",
            "didn't",
            "wouldn't have"
          ],
          a: "hadn't",
          tip: "Marrying young was a PAST event she regrets → WISH + PAST PERFECT negative. 'She wishes she HADN'T married so young.' DIDN'T = present wish ❌."
        },
        {
          type: "mc",
          q: "What tense NEVER follows WISH?",
          options: [
            "Will / future forms.",
            "Past simple.",
            "Past perfect."
          ],
          a: "Will / future forms.",
          tip: "WISH + WILL ❌ — this is the most common error. 'I wish it will rain' ❌ → 'I wish it would rain' ✅ (WOULD, not WILL — would is the past form of will)."
        },
        {
          type: "mc",
          q: "Complete: 'He wished he ___ dropped out of university, but he ___ he could still go back.'",
          options: [
            "hadn't / hoped",
            "didn't / hoped",
            "hadn't / wished"
          ],
          a: "hadn't / hoped",
          tip: "Past regret: HADN'T DROPPED OUT (past perfect). Future possibility: HOPED HE COULD (real option). The pairing WISH (past) + HOPE (future) is the emotional core of Unit 15."
        },
        {
          type: "mc",
          q: "Unit 15 + Unit 11 combined: 'She told me that she ___ she ___ chosen a different career.'",
          options: [
            "wished / had",
            "hoped / had",
            "wished / have"
          ],
          a: "wished / had",
          tip: "TOLD ME THAT (Unit 11 reported speech) + WISHED SHE HAD CHOSEN (Unit 15 past regret reported). Double backshift: wish stays as 'wished', had chosen = past perfect in reported context."
        },
        {
          type: "mc",
          q: "Final Unit 15 rule check: Match correctly — 'I wish I worked' / 'I wish I had worked' / 'I hope I can work'.",
          options: [
            "Present unreal / Past regret / Real future possibility.",
            "Past regret / Present unreal / Real future possibility.",
            "Present unreal / Real future possibility / Past regret."
          ],
          a: "Present unreal / Past regret / Real future possibility.",
          tip: "WISH + past simple = PRESENT UNREAL. WISH + past perfect = PAST REGRET. HOPE + can/will = REAL FUTURE POSSIBILITY. The three pillars of Unit 15."
        }
      ]
    },

    // ===================================================================
    // REVIEW SET 5 — INTEGRATION (all five units together)
    // ===================================================================
    {
      id: "m10-rev-set5",
      title: "Review Set 5: Full Module Integration",
      context: `*All five units — Mixed questions combining structures across the module.*`,

      questions: [

        {
          type: "mc",
          q: "Which sentence correctly combines reported speech (U11) and an indirect question (U14)?",
          options: [
            "She said she didn't know where the nearest hospital was.",
            "She said she didn't know where was the nearest hospital.",
            "She told that she didn't know where was the nearest hospital."
          ],
          a: "She said she didn't know where the nearest hospital was.",
          tip: "SAID (no object ✅) + DIDN'T KNOW WHERE + subject + verb (no inversion ✅). 'Where WAS the nearest hospital' = correct embedded order. 'Where WAS' not 'where was'."
        },
        {
          type: "mc",
          q: "Which sentence correctly combines a wish (U15) and an if/whether clause (U13)?",
          options: [
            "She wished she knew whether to stay or leave.",
            "She wished she knew if she stay or leave.",
            "She hoped she knew whether stay or leave."
          ],
          a: "She wished she knew whether to stay or leave.",
          tip: "WISHED SHE KNEW (Unit 15 present wish: she doesn't know NOW) + WHETHER TO STAY (Unit 13 embedded uncertainty). Clean combination of both units."
        },
        {
          type: "mc",
          q: "Which sentence correctly combines a reported question (U12) and verb + preposition (U13)?",
          options: [
            "He asked her what she had always dreamed of doing.",
            "He asked her what had she always dreamed of doing.",
            "He asked her what she has always dreamed about doing."
          ],
          a: "He asked her what she had always dreamed of doing.",
          tip: "ASKED WHAT + subject + verb (no inversion ✅) + DREAMED OF + gerund ✅. 'Dreamed of doing' — the preposition OF belongs to the collocation, not to the question structure."
        },
        {
          type: "mc",
          q: "Identify the units used: 'He told me he had been wondering whether to apply for the job.'",
          options: [
            "Unit 11 (told me / had been wondering — reported speech) + Unit 13 (whether to apply — embedded clause).",
            "Unit 12 (reported question) + Unit 14 (indirect question).",
            "Unit 15 (wish) + Unit 11 (reported speech)."
          ],
          a: "Unit 11 (told me / had been wondering — reported speech) + Unit 13 (whether to apply — embedded clause).",
          tip: "TOLD ME + HAD BEEN WONDERING = reported speech (Unit 11 backshift: present perfect continuous → past perfect continuous). WHETHER TO APPLY = Unit 13 embedded clause of uncertainty."
        },
        {
          type: "mc",
          q: "Identify the units used: 'Could you tell me if she wishes she had taken the job?'",
          options: [
            "Unit 14 (could you tell me — indirect question) + Unit 15 (wishes she had taken — past regret).",
            "Unit 12 (reported question) + Unit 13 (if/whether clause).",
            "Unit 11 (reported speech) + Unit 14 (indirect question)."
          ],
          a: "Unit 14 (could you tell me — indirect question) + Unit 15 (wishes she had taken — past regret).",
          tip: "COULD YOU TELL ME IF = indirect question opener (Unit 14). SHE WISHES SHE HAD TAKEN = Unit 15 past regret (past perfect after wish). Two units in one polite question."
        },
        {
          type: "mc",
          q: "What is wrong with: 'She asked me if I wished I would have studied harder'?",
          options: [
            "'Would have' is wrong after WISH — should be 'had studied' (wish + past perfect).",
            "'If' is wrong — should be 'whether'.",
            "'Asked me' is wrong — should be 'told me'."
          ],
          a: "'Would have' is wrong after WISH — should be 'had studied' (wish + past perfect).",
          tip: "WISH + WOULD HAVE ❌ — a very common error. Past regret = WISH + PAST PERFECT: 'she wished she HAD studied harder.' WOULD HAVE belongs in conditionals, not after WISH."
        },
        {
          type: "mc",
          q: "Transform into a full multi-unit sentence: He regrets not learning to cook. He is still able to learn.",
          options: [
            "He wishes he had learned to cook, but he hopes he still can.",
            "He wished he didn't learn to cook, but he hoped he still could.",
            "He wishes he could cook, and he hopes he would learn."
          ],
          a: "He wishes he had learned to cook, but he hopes he still can.",
          tip: "Past regret: WISHES HE HAD LEARNED (past perfect ✅). Future possibility: HOPES HE STILL CAN (real, achievable ✅). The perfect wish-hope pairing from Unit 15."
        },
        {
          type: "mc",
          q: "What is the correct reported form of: 'Do you know whether the results are out yet?'",
          options: [
            "She asked him if he knew whether the results were out yet.",
            "She asked him did he know whether the results are out yet.",
            "She told him if he knew whether the results were out yet."
          ],
          a: "She asked him if he knew whether the results were out yet.",
          tip: "Reporting an indirect question: ASKED HIM IF HE KNEW (backshift: know → knew) + WHETHER THE RESULTS WERE (backshift: are → were). Three units layered: 11 (reporting) + 13 (whether) + 12 (reported)."
        },
        {
          type: "mc",
          q: "Which is the most natural and grammatically correct combination of all five units?",
          options: [
            "She told him she had always wondered whether she had made the right choices, and asked if he knew what she could have done differently — she wished she had believed in herself more, but she hoped it wasn't too late.",
            "She said to him she wondered whether she made the right choices, asked if he knows what she could do differently — she wishes she believed in herself more, and hoped it isn't too late.",
            "She had told to him that she was wondering if she made right choices, and asked whether he knew what could she have done differently — she wished she had believed in herself, but she hoped it isn't too late."
          ],
          a: "She told him she had always wondered whether she had made the right choices, and asked if he knew what she could have done differently — she wished she had believed in herself more, but she hoped it wasn't too late.",
          tip: "TOLD HIM (U11) + HAD WONDERED WHETHER (U13 + U11 backshift) + ASKED IF HE KNEW WHAT (U12 + U14) + WISHED SHE HAD BELIEVED IN (U15 + U13 collocation) + HOPED IT WASN'T TOO LATE (U15). All five units, one sentence. This is real English. 🏆"
        },
        {
          type: "mc",
          q: "Which unit deals with the SOCIAL function of politeness in questions?",
          options: [
            "Unit 14 — Indirect Questions.",
            "Unit 12 — Reported Questions.",
            "Unit 13 — If/Whether clauses."
          ],
          a: "Unit 14 — Indirect Questions.",
          tip: "Unit 14's purpose is POLITENESS: 'Could you tell me...', 'Do you know...', 'I was wondering if...' — these soften direct questions for formal or polite contexts."
        },
        {
          type: "mc",
          q: "Which unit deals with the EMOTIONAL function of expressing regret and longing?",
          options: [
            "Unit 15 — Wishes & Regrets.",
            "Unit 11 — Reported Speech.",
            "Unit 13 — If/Whether clauses."
          ],
          a: "Unit 15 — Wishes & Regrets.",
          tip: "Unit 15's core function is emotional: WISH expresses what we want but don't have / what we did but regret. HOPE expresses what we still believe is possible. The grammar of human longing."
        },
        {
          type: "mc",
          q: "Module 10 final question: Which statement about Module 10 is TRUE?",
          options: [
            "All five units involve embedding one clause inside another — reported speech, questions, if/whether, indirect questions, and wishes all use embedded structures.",
            "Each unit has completely independent grammar with no overlap between units.",
            "Only Units 11 and 12 use tense backshift — the other units do not."
          ],
          a: "All five units involve embedding one clause inside another — reported speech, questions, if/whether, indirect questions, and wishes all use embedded structures.",
          tip: "The unifying theme of Module 10: EMBEDDED CLAUSES. Every unit — reported speech (said that), reported questions (asked if), if/whether (wonder whether), indirect questions (do you know where), wishes (wish + past) — puts one clause inside another. 🎯🏆",
          successTip: "🏆 MÓDULO 10 — REVIEW GENERAL COMPLETE! ✅ Unit 11: Reported Speech (said/told + backshift) ✅ Unit 12: Reported Questions (asked if/whether/wh + no inversion) ✅ Unit 13: If/Whether + Verb+Preposition collocations ✅ Unit 14: Indirect Questions (polite embedded forms) ✅ Unit 15: Wishes & Regrets (past simple / past perfect / could / if only / hope) 🎯 One module. Five units. One core skill: embedding clauses inside clauses. That is advanced English. 🌟"
        }
      ]
    }
  ]
};
